import { useEffect, useRef } from "react";
import { useT } from "@/i18n/LocaleContext";

/* CinematicHero — universal full-viewport page opener.
   Layers, from back to front:
     .hero-parallax  → outer wrapper that takes the scroll-parallax translate
       .hero-media   → <img> with Ken Burns (scale 1.00→1.06, 80s loop)
     .hero-scrim     → dark gradient (breath animates the top stop 0.50↔0.60, 25s)
     .hero-scrim-corner → soft radial darkening in the bottom-left text well
     .hero-atmos     → drifting cream radial highlight (45s, soft-light blend)
     .hero-content   → eyebrow / title / subtitle stack, anchored bottom-left
     .hero-scroll-cue → optional scroll-to-content affordance (bottom-right)

   Motion respects prefers-reduced-motion (see index.css). */
export const CinematicHero = ({
  imageSrc,
  imageAlt,
  eyebrow,
  title,
  subtitle,
  scrollTarget = "content",
  scrollLabelKey = "hero.scroll",
  testIdPrefix = "hero",
}) => {
  const { t } = useT();
  const parallaxRef = useRef(null);

  // Scroll parallax — image translates upward at ~85% of scroll speed.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mql.matches) return;
    const el = parallaxRef.current;
    if (!el) return;

    let queued = false;
    let rafId = 0;

    const update = () => {
      queued = false;
      const y = window.scrollY || 0;
      // 85% of scroll speed = image lags by 15% of scroll; but for the
      // classic "image moves upward slower than the page" feel, we translate
      // the image DOWN by 15% of scrollY relative to the page (making it
      // effectively scroll at 85% speed while the page scrolls at 100%).
      const offset = y * -0.15;
      el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    };

    const onScroll = () => {
      if (!queued) {
        queued = true;
        rafId = requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const onCueClick = (ev) => {
    ev.preventDefault();
    const target = document.getElementById(scrollTarget);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollLabel = t(scrollLabelKey) || "Scroll";

  return (
    <>
      <section
        className="cinematic-hero"
        data-testid={`${testIdPrefix}-cinematic`}
        aria-label={title}
      >
        <div ref={parallaxRef} className="hero-parallax">
          <img
            src={imageSrc}
            alt={imageAlt}
            className="hero-media"
            loading="eager"
            fetchPriority="high"
            data-testid={`${testIdPrefix}-media`}
          />
        </div>
        <div className="hero-scrim" aria-hidden />
        <div className="hero-scrim-corner" aria-hidden />
        <div className="hero-atmos" aria-hidden />
        <div className="hero-camera-glide" aria-hidden />

        <div className="hero-content">
          <div className="hero-content-inner">
            {eyebrow && (
              <p className="hero-eyebrow" data-testid={`${testIdPrefix}-eyebrow`}>
                {eyebrow}
              </p>
            )}
            <h1 className="hero-title" data-testid={`${testIdPrefix}-title`}>
              {title}
            </h1>
            {subtitle && (
              <p className="hero-subtitle" data-testid={`${testIdPrefix}-subtitle`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <a
          href={`#${scrollTarget}`}
          onClick={onCueClick}
          className="hero-scroll-cue"
          data-testid={`${testIdPrefix}-scroll-cue`}
          aria-label={scrollLabel}
        >
          {scrollLabel}
        </a>
      </section>
      <div className="cinematic-hero-fade" aria-hidden />
    </>
  );
};

export default CinematicHero;
